import { useState } from 'react'
import { X, Copy, Check } from 'lucide-react'
import { useAuth } from '../auth/AuthProvider'
import { createOrganization } from '../../lib/organization'
import { SessionError } from '../../lib/session'

interface CreateOrgModalProps {
  onClose: () => void
  onCreated: (orgId: string) => void
}

export default function CreateOrgModal({ onClose, onCreated }: CreateOrgModalProps) {
  const { user } = useAuth()
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ orgId: string; inviteToken: string } | null>(null)
  const [copied, setCopied] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!user) return
    setError('')
    setLoading(true)
    try {
      const res = await createOrganization(name.trim(), user.uid, user.displayName || 'Member')
      setResult(res)
    } catch (err) {
      setError(
        err instanceof SessionError && err.code === 'SIGN_IN_REQUIRED'
          ? err.message
          : 'Failed to create team. Please try again.',
      )
    } finally {
      setLoading(false)
    }
  }

  function handleCopy() {
    if (!result) return
    navigator.clipboard.writeText(`${window.location.origin}/org/join/${result.inviteToken}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
      <div className="card card-shadow w-full max-w-md p-8 relative animate-modal-enter max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-navy/70 hover:text-navy transition" aria-label="Close">
          <X size={20} />
        </button>

        <h2 className="hl text-2xl mb-6">Create a Team</h2>

        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mono-label text-navy/70 mb-1.5">Team Name</label>
              <input
                required
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Marketing team"
                className="w-full border-2 border-navy/55 focus:border-navy px-3.5 py-2.5 text-sm bg-paper outline-none"
              />
            </div>
            {error && <p className="text-brick text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-amber w-full"
            >
              {loading ? 'Creating' : 'Create the team'}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-paper border-2 border-teal p-4 text-center">
              <p className="text-sm text-teal font-semibold mb-2">Team created</p>
              <p className="text-xs text-teal mb-3">Send this invite link to your team. Anyone with it can join, so share it directly.</p>
              <button
                onClick={handleCopy}
                className="btn w-full"
              >
                {copied ? <Check className="w-4 h-4 text-teal" /> : <Copy className="w-4 h-4 text-navy/70" />}
                {copied ? 'Invite link copied' : 'Copy invite link'}
              </button>
            </div>
            <button
              onClick={() => onCreated(result.orgId)}
              className="btn btn-navy w-full"
            >
              Go to the dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
